import { API_BASE_URL, BACKEND_URL, updateEnvironmentConfig } from '../config/environment.js';

// Local API setup utility with environment variable support
class LocalApiSetup {
  constructor() {
    // Initialize with environment-based URL
    this.baseUrl = API_BASE_URL;
    this.configLoaded = false;
    console.log('🌐 Using API setup with environment variables');
  }

  // Load environment configuration from backend
  async loadEnvironmentConfig() {
    try {
      console.log('🔍 Loading environment configuration...');
      const newBaseUrl = await this.getApiBaseUrlFromEnv();
      
      if (newBaseUrl !== this.baseUrl) {
        this.baseUrl = newBaseUrl;
        updateEnvironmentConfig({ API_BASE_URL: newBaseUrl });
        console.log('🌐 Updated API base URL from environment:', this.baseUrl);
      }
      
      this.configLoaded = true;
      console.log('✅ Environment configuration loaded successfully');
    } catch (error) {
      console.error('❌ Error loading environment config:', error);
      // Don't throw, just use the default
      this.configLoaded = true;
    }
  }

  // Get API base URL from environment variables
  async getApiBaseUrlFromEnv() {
    console.log('🌐 getApiBaseUrlFromEnv called');
    console.log('🌐 Current window location:', {
      hostname: window.location.hostname,
      protocol: window.location.protocol,
      port: window.location.port,
      origin: window.location.origin,
      href: window.location.href
    });
    
    // Check for environment variables (for production builds)
    if (process.env.REACT_APP_API_BASE_URL) {
      console.log('📋 Using REACT_APP_API_BASE_URL from environment');
      return process.env.REACT_APP_API_BASE_URL;
    }
    
    // Check for runtime environment variables (for development)
    if (window.ENV && window.ENV.API_BASE_URL) {
      console.log('📋 Using window.ENV.API_BASE_URL');
      return window.ENV.API_BASE_URL;
    }
    
    // Check localStorage for dynamic configuration
    // BUT: Always use proxy for localhost, ignore localStorage
    const storedApiUrl = localStorage.getItem('API_BASE_URL');
    if (storedApiUrl && !this.isLocal()) {
      console.log('📋 Using API_BASE_URL from localStorage (non-localhost)');
      return storedApiUrl;
    } else if (storedApiUrl && this.isLocal()) {
      console.log('⚠️ Ignoring localStorage API_BASE_URL for localhost - using proxy');
      localStorage.removeItem('API_BASE_URL'); // Clean up stale data
    }
    
    // Try to fetch from backend environment endpoint
    try {
      console.log('🔍 Fetching configuration from backend...');
      const backendUrl = this.getBackendUrl();
      console.log('🔍 Backend URL for config fetch:', backendUrl);
      const response = await fetch(`${backendUrl}/api/config/environment`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        // Add timeout to prevent hanging
        signal: AbortSignal.timeout(10000)
      });
      
      if (response.ok) {
        const config = await response.json();
        console.log('📋 Received configuration from backend:', config);
        // Store the configuration for future use
        updateEnvironmentConfig(config);
        return config.API_BASE_URL;
      } else {
        console.log('⚠️ Backend config endpoint returned status:', response.status);
      }
    } catch (error) {
      console.log('⚠️ Could not fetch environment config from backend:', error.message);
    }
    
    // Default fallback - use the proxy configuration or construct from current location
    if (this.isLocal()) {
      console.log('📋 Using default API_BASE_URL with proxy for localhost');
      return '/api';
    } else {
      // For non-localhost access, we need to determine the correct backend URL
      // Try to fetch from the backend environment endpoint first
      try {
        console.log('🔍 Attempting to fetch backend config from current origin...');
        const response = await fetch(`${window.location.origin}/api/config/environment`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          signal: AbortSignal.timeout(5000)
        });
        
        if (response.ok) {
          const config = await response.json();
          console.log('📋 Received config from current origin:', config);
          updateEnvironmentConfig(config);
          return config.API_BASE_URL;
        }
      } catch (error) {
        console.log('⚠️ Could not fetch from current origin, trying fallback...');
      }
      
      // Fallback: try to construct the API URL
      // For production, use the same origin with /api path
      // For development, use the proxy (same origin)
      const apiUrl = `${window.location.origin}/api`;
      console.log('📋 Using fallback API_BASE_URL:', apiUrl);
      return apiUrl;
    }
  }

  // Get the API base URL
  getApiBaseUrl() {
    if (!this.configLoaded) {
      console.log('⚠️ Configuration not loaded yet, using default');
      return '/api';
    }
    
    // For localhost development, always use the proxy
    if (this.isLocal()) {
      console.log('🌐 Using proxy for localhost development');
      return '/api';
    }
    
    console.log('🌐 Current API base URL:', this.baseUrl);
    return this.baseUrl;
  }

  // Get backend URL for API calls
  getBackendUrl() {
    console.log('🌐 getBackendUrl called');
    console.log('🌐 Current window location:', {
      hostname: window.location.hostname,
      protocol: window.location.protocol,
      port: window.location.port,
      origin: window.location.origin
    });
    
    // Check for environment variables
    if (process.env.REACT_APP_BACKEND_URL) {
      console.log('📋 Using REACT_APP_BACKEND_URL from environment');
      return process.env.REACT_APP_BACKEND_URL;
    }
    
    // Check localStorage for dynamic configuration
    const storedBackendUrl = localStorage.getItem('BACKEND_URL');
    if (storedBackendUrl) {
      console.log('📋 Using BACKEND_URL from localStorage');
      return storedBackendUrl;
    }
    
    // If we're not on localhost, use the same origin
    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      const dynamicBackendUrl = window.location.origin;
      console.log('📋 Using dynamic backend URL for production:', dynamicBackendUrl);
      return dynamicBackendUrl;
    }
    
    // For localhost development, use the proxy (same origin)
    console.log('📋 Using proxy for localhost development');
    return window.location.origin;
  }

  // Check if we're running locally
  isLocal() {
    return window.location.hostname === 'localhost' || 
           window.location.hostname === '127.0.0.1' ||
           window.location.hostname.includes('localhost');
  }

  // Set base URL (for testing)
  setBaseUrl(url) {
    this.baseUrl = url;
    updateEnvironmentConfig({ API_BASE_URL: url });
    console.log('🌐 Updated API base URL:', this.baseUrl);
  }

  // Set backend URL
  setBackendUrl(url) {
    updateEnvironmentConfig({ BACKEND_URL: url });
    console.log('🌐 Updated backend URL:', url);
  }

  // Clear any stored external URLs
  clearExternalUrls() {
    try {
      localStorage.removeItem('externalUrls');
      console.log('🧹 Cleared external URLs from localStorage');
    } catch (error) {
      console.error('❌ Error clearing external URLs:', error);
    }
  }

  // Get environment configuration
  getEnvironmentConfig() {
    return {
      API_BASE_URL: this.getApiBaseUrl(),
      BACKEND_URL: this.getBackendUrl(),
      FRONTEND_URL: process.env.REACT_APP_FRONTEND_URL || window.ENV?.FRONTEND_URL || 'http://localhost:3000'
    };
  }

  // Check if configuration is loaded
  isConfigLoaded() {
    return this.configLoaded;
  }
}

// Create and export a singleton instance
const localApiSetup = new LocalApiSetup();
export default localApiSetup; 